import React, { useEffect, useState, useRef } from 'react';
import * as d3 from 'd3';
import './css/Visualization4.css';

const OverlayButton = ({ onClick, isOpen }) => (
  <button
    onClick={onClick}
    className="chart-overlay-button"
  >
    {isOpen ? 'Close' : 'Edit countries and regions'}
  </button>
);

const CountrySelectionOverlay = ({ countries, selectedCountries, toggleCountry, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="chart-country-selection-overlay">
      <h3>Select countries or regions</h3>
      <input
        type="text"
        placeholder="Search countries..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="country-search-input"
      />
      <div className="country-list">
        {filteredCountries.map((country, index) => (
          <div key={country} className={`country-option ${index % 2 === 0 ? 'even' : 'odd'}`}>
            <label>
              <input
                type="checkbox"
                checked={selectedCountries.includes(country)}
                onChange={() => toggleCountry(country)}
              />
              {country}
            </label>
          </div>
        ))}
      </div>
      <button onClick={onClose} className="close-button">Close</button>
    </div>
  );
};

const Legend = ({ data }) => {
  return (
    <div className="legend-container">
      <h3>Legend</h3>
      {data.map((item, index) => (
        <div key={index} className="legend-item">
          <span className="legend-color" style={{ backgroundColor: item.color }}></span>
          <span className="legend-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

function Visualization4() {
  const [data, setData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [legendData, setLegendData] = useState([]);
  const svgRef = useRef(null);

  useEffect(() => {
    d3.csv(`${process.env.PUBLIC_URL}/data/air-passengers.csv`).then((loadedData) => {
      const parsedData = loadedData.map(d => ({
        country: d.Entity,
        code: d.Code,
        year: +d.Year,
        trips: +d['passengers'],
      }));
      setData(parsedData);
      const uniqueCountries = [...new Set(parsedData.map(d => d.country))];
      setCountries(uniqueCountries);
      setSelectedCountries([uniqueCountries[195]]);
    });
  }, []);

  useEffect(() => {
    if (data.length > 0 && selectedCountries.length > 0) {
      drawLineChart();
    }
  }, [data, selectedCountries]);

  const drawLineChart = () => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const margin = { top: 20, right: 30, bottom: 50, left: 80 };
    const width = 800 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    const x = d3.scaleLinear()
      .domain([1970, 2021])
      .range([0, width]);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data.filter(d => selectedCountries.includes(d.country)), d => d.trips)])
      .range([height, 0]);

    const color = d3.scaleOrdinal(d3.schemeCategory10);

    const line = d3.line()
      .x(d => x(d.year))
      .y(d => y(d.trips));

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(10).tickFormat(d3.format("d")))
      .selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", "-.8em")
        .attr("dy", ".15em")
        .attr("transform", "rotate(-45)");

    const formatYAxis = (d) => {
      if (d >= 1e9) return (d / 1e9).toFixed(1) + "B";
      if (d >= 1e6) return (d / 1e6).toFixed(1) + "M";
      if (d >= 1e3) return (d / 1e3).toFixed(1) + "K";
      return d;
    };

    g.append("g")
      .call(d3.axisLeft(y).tickFormat(formatYAxis));

    // Tooltip setup
    const tooltip = d3.select("body").append("div")
      .attr("class", "tooltip")
      .style("opacity", 0);

    const newLegendData = [];

    selectedCountries.forEach(country => {
      const countryData = data.filter(d => d.country === country);
      const countryColor = color(country);
      
      // Draw the line
      g.append("path")
        .datum(countryData)
        .attr("fill", "none")
        .attr("stroke", countryColor)
        .attr("stroke-width", 1.5)
        .attr("d", line);

      // Draw the data points
      g.selectAll(`.datapoint-${country}`)
        .data(countryData)
        .enter().append("circle")
        .attr("class", `datapoint-${country}`)
        .attr("cx", d => x(d.year))
        .attr("cy", d => y(d.trips))
        .attr("r", 4)
        .attr("fill", countryColor)
        .on("mouseover", function(event, d) {
          d3.select(this)
            .transition()
            .duration(200)
            .attr("r", 6)
            .style("stroke", "black")
            .style("stroke-width", "2");
          
          tooltip.transition()
            .duration(200)
            .style("opacity", 0.9);
          tooltip.html(`${country}<br>Year: ${d.year}<br>Passengers: ${d.trips.toLocaleString()}`)
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 28) + "px");
        })
        .on("mouseout", function() {
          d3.select(this)
            .transition()
            .duration(200)
            .attr("r", 4)
            .style("stroke", "none");
          
          tooltip.transition()
            .duration(500)
            .style("opacity", 0);
        });

      newLegendData.push({ label: country, color: countryColor });
    });

    setLegendData(newLegendData);

    g.append("text")
      .attr("x", width / 2)
      .attr("y", height + margin.bottom - 10)
      .style("text-anchor", "middle")
      .text("Year");

    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left + 20)
      .attr("x", 0 - (height / 2))
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text("Number of Passengers");
  };

  const toggleCountry = (country) => {
    setSelectedCountries(prev => 
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const toggleOverlay = () => {
    setIsOverlayOpen(!isOverlayOpen);
  };

  return (
    <div className="visualization-container">
      <div className="header-container">
        <h2>Air Passengers (1970-2021)</h2>
        <p>Domestic and international passengers of airlines registered in the country. Passengers counted each time they travel.</p>
      </div>
      <div className="chart-wrapper">
        <div className="chart-controls-and-overlay">
          <div className="chart-controls">
            <OverlayButton onClick={toggleOverlay} isOpen={isOverlayOpen} />
          </div>
          {isOverlayOpen && (
            <CountrySelectionOverlay
              countries={countries}
              selectedCountries={selectedCountries}
              toggleCountry={toggleCountry}
              onClose={toggleOverlay}
            />
          )}
        </div>
        <div className="chart-content">
          <div className="chart-and-legend">
            <svg ref={svgRef} width={800} height={400}></svg>
            <Legend data={legendData} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Visualization4;